import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { getRequestTypeLabel } from '@/lib/attendanceTypes';

// Module-level constants - never recreated
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const CHART_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#14b8a6'];

// Average daily cost for liability calculation (configurable per company in future)
const AVG_DAILY_COST = 150;

/**
 * Hook to load attendance reports data with single optimized RPC call
 * Replaces 4 queries + massive client-side computation
 *
 * @param {string} companyId - Company ID
 * @param {number} year - Year to generate report for
 * @returns {Object} - Report data, loading state, and helpers
 */
export function useAttendanceReports(companyId, year) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load report data using the optimized RPC
   */
  const loadData = useCallback(async () => {
    if (!companyId || !year) {
      console.log('[useAttendanceReports] Missing companyId or year');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useAttendanceReports] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useAttendanceReports] Loading data with single RPC call...');
      console.time('[useAttendanceReports] RPC call duration');

      // Single RPC call replaces 4 queries + client-side computation!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_attendance_reports_page',
        {
          p_company_id: companyId,
          p_year: parseInt(year)
        }
      );

      console.timeEnd('[useAttendanceReports] RPC call duration');

      if (rpcError) {
        console.error('[useAttendanceReports] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useAttendanceReports] RPC response:', {
        balanceStats: rpcData?.balance_stats,
        requestStats: rpcData?.request_stats,
        monthlyTrendsCount: rpcData?.monthly_trends?.length || 0,
        byLeaveTypeCount: rpcData?.by_leave_type?.length || 0
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useAttendanceReports] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, year]);

  // Auto-load when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  // Computed metrics
  const metrics = useMemo(() => {
    if (!data) {
      return {
        leaveLibability: 0,
        absenteeismRate: 0,
        utilizationRate: 0,
        sickLeaveRate: 0
      };
    }

    const balanceStats = data.balance_stats || {};
    const requestStats = data.request_stats || {};
    const meta = data.meta || {};

    const totalRemaining = balanceStats.total_remaining || 0;
    const totalEntitled = balanceStats.total_entitled || 0;
    const totalTaken = balanceStats.total_taken || 0;
    const totalUsers = balanceStats.total_users || 0;
    const workingDaysInYear = meta.working_days_in_year || 260;

    // Leave Liability (financial)
    const leaveLibability = totalRemaining * AVG_DAILY_COST;

    // Absenteeism Rate = (Days Absent / Available Working Days) × 100
    const totalWorkingDays = totalUsers * workingDaysInYear;
    const absenteeismRate = totalWorkingDays > 0
      ? ((totalTaken / totalWorkingDays) * 100).toFixed(2)
      : 0;

    // Leave Utilization Rate = (Taken / Entitled) × 100
    const utilizationRate = totalEntitled > 0
      ? ((totalTaken / totalEntitled) * 100).toFixed(1)
      : 0;

    // Sick Leave Rate (from by_leave_type data)
    const sickLeaveData = (data.by_leave_type || []).find(t =>
      t.type === 'Sick Leave' || t.type === 'sick_leave'
    );
    const sickDays = sickLeaveData?.approved || 0;
    const sickLeaveRate = totalWorkingDays > 0
      ? ((sickDays / totalWorkingDays) * 100).toFixed(2)
      : 0;

    return {
      leaveLibability,
      absenteeismRate: parseFloat(absenteeismRate),
      utilizationRate: parseFloat(utilizationRate),
      sickLeaveRate: parseFloat(sickLeaveRate)
    };
  }, [data]);

  // Overview data (for backwards compatibility with exports)
  const overview = useMemo(() => {
    if (!data) {
      return {
        totalEntitled: 0,
        totalTaken: 0,
        totalRemaining: 0,
        totalUsers: 0,
        totalRequests: 0,
        pendingRequests: 0,
        approvedRequests: 0,
        rejectedRequests: 0,
        averageUsage: 0
      };
    }

    const balanceStats = data.balance_stats || {};
    const requestStats = data.request_stats || {};

    return {
      totalEntitled: balanceStats.total_entitled || 0,
      totalTaken: balanceStats.total_taken || 0,
      totalRemaining: balanceStats.total_remaining || 0,
      totalUsers: balanceStats.total_users || 0,
      totalRequests: requestStats.total || 0,
      pendingRequests: requestStats.pending || 0,
      approvedRequests: requestStats.approved || 0,
      rejectedRequests: requestStats.rejected || 0,
      averageUsage: metrics.utilizationRate
    };
  }, [data, metrics.utilizationRate]);

  // Approval efficiency
  const approvalEfficiency = useMemo(() => {
    if (!data?.approval_efficiency) {
      return { avgApprovalTime: 0, pendingOverdue: 0 };
    }
    return {
      avgApprovalTime: data.approval_efficiency.avg_approval_hours || 0,
      pendingOverdue: data.approval_efficiency.overdue_count || 0
    };
  }, [data?.approval_efficiency]);

  // At-risk employees (combined for backwards compatibility)
  const atRiskEmployees = useMemo(() => {
    return {
      burnoutRisk: data?.burnout_risk || [],
      lowBalance: data?.low_balance || []
    };
  }, [data?.burnout_risk, data?.low_balance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Raw data from RPC
    data,
    loading,
    error,
    refetch: loadData,

    // Pre-computed metrics
    metrics,
    overview,
    approvalEfficiency,
    atRiskEmployees,

    // Direct accessors (pre-computed by RPC)
    monthlyTrends: data?.monthly_trends || [],
    byLeaveType: data?.by_leave_type || [],
    forfeitureRisk: data?.forfeiture_risk || [],
    upcomingAbsences: data?.upcoming_absences || [],
    peakAbsenceDays: data?.peak_absence_days || [],
    dayOfWeekPattern: data?.day_of_week_pattern || [],

    // Raw data for exports
    balances: data?.balances || [],
    requests: data?.requests || [],

    // Static data (module-level constants)
    years: YEARS,
    chartColors: CHART_COLORS
  };
}

/**
 * Lazy-loaded export utilities
 * Only imports heavy libraries when needed
 */
export const exportUtils = {
  /**
   * Export to CSV
   */
  async exportCSV(reportData, companyName, year) {
    const { metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
            forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
            dayOfWeekPattern, balances, requests } = reportData;

    const csvRows = [];

    // Header
    csvRows.push('Attendance Report');
    csvRows.push(`Company: ${companyName || ''}`);
    csvRows.push(`Year: ${year}`);
    csvRows.push(`Generated: ${new Date().toLocaleString('en-GB')}`);
    csvRows.push('');

    // Key Metrics
    csvRows.push('KEY METRICS');
    csvRows.push('Metric,Value');
    csvRows.push(`Absenteeism Rate,${metrics.absenteeismRate}%`);
    csvRows.push(`Sick Leave Rate,${metrics.sickLeaveRate}%`);
    csvRows.push(`Leave Utilization,${metrics.utilizationRate}%`);
    csvRows.push(`Leave Liability,£${metrics.leaveLibability.toLocaleString()}`);
    csvRows.push('');

    // Overview
    csvRows.push('OVERVIEW');
    csvRows.push('Metric,Value');
    csvRows.push(`Total Users,${overview.totalUsers}`);
    csvRows.push(`Total Entitled Days,${overview.totalEntitled}`);
    csvRows.push(`Total Taken Days,${overview.totalTaken}`);
    csvRows.push(`Total Remaining Days,${overview.totalRemaining}`);
    csvRows.push(`Total Requests,${overview.totalRequests}`);
    csvRows.push(`Approved Requests,${overview.approvedRequests}`);
    csvRows.push(`Rejected Requests,${overview.rejectedRequests}`);
    csvRows.push(`Pending Requests,${overview.pendingRequests}`);
    csvRows.push('');

    // Approval Efficiency
    csvRows.push('APPROVAL EFFICIENCY');
    csvRows.push('Metric,Value');
    csvRows.push(`Average Approval Time,${approvalEfficiency.avgApprovalTime} hours`);
    csvRows.push(`Pending Requests,${overview.pendingRequests}`);
    csvRows.push(`Overdue Requests (>48h),${approvalEfficiency.pendingOverdue}`);
    csvRows.push('');

    // By Leave Type
    csvRows.push('BY LEAVE TYPE');
    csvRows.push('Type,Total,Approved,Rejected,Pending');
    byLeaveType.forEach(t => {
      csvRows.push(`${t.type},${t.count},${t.approved},${t.rejected},${t.pending}`);
    });
    csvRows.push('');

    // Monthly Trends
    csvRows.push('MONTHLY TRENDS');
    csvRows.push('Month,Total Requests,Approved,Rejected');
    monthlyTrends.forEach(m => {
      csvRows.push(`${m.monthName},${m.requests},${m.approved},${m.rejected}`);
    });
    csvRows.push('');

    // Forfeiture Risk
    if (forfeitureRisk.length > 0) {
      csvRows.push('FORFEITURE RISK');
      csvRows.push('Employee,Days at Risk,Days Until Year End');
      forfeitureRisk.forEach(emp => {
        csvRows.push(`${emp.name},${emp.daysAtRisk},${emp.daysUntilYearEnd}`);
      });
      csvRows.push('');
    }

    // Upcoming Absences
    if (upcomingAbsences.length > 0) {
      csvRows.push('UPCOMING ABSENCES (NEXT 30 DAYS)');
      csvRows.push('Date,Employee,Type,Days');
      upcomingAbsences.forEach(abs => {
        csvRows.push(`${abs.date},${abs.name},${abs.type},${abs.days}`);
      });
      csvRows.push('');
    }

    // Burnout Risk
    if (atRiskEmployees.burnoutRisk.length > 0) {
      csvRows.push('BURNOUT RISK (LOW LEAVE USAGE)');
      csvRows.push('Employee,Remaining Days,Usage Rate');
      atRiskEmployees.burnoutRisk.forEach(emp => {
        csvRows.push(`${emp.name},${emp.remaining},${emp.usageRate}%`);
      });
      csvRows.push('');
    }

    // Low Balance Employees
    if (atRiskEmployees.lowBalance.length > 0) {
      csvRows.push('LOW BALANCE ALERT');
      csvRows.push('Employee,Remaining Days,Usage Rate');
      atRiskEmployees.lowBalance.forEach(emp => {
        csvRows.push(`${emp.name},${emp.remaining},${emp.usageRate}%`);
      });
      csvRows.push('');
    }

    // Peak Absence Days
    if (peakAbsenceDays.length > 0) {
      csvRows.push('PEAK ABSENCE DAYS');
      csvRows.push('Date,Employees Absent');
      peakAbsenceDays.forEach(peak => {
        csvRows.push(`${peak.date},${peak.count}`);
      });
      csvRows.push('');
    }

    // Sick Leave Pattern
    csvRows.push('SICK LEAVE PATTERN BY DAY');
    csvRows.push('Day of Week,Sick Days');
    dayOfWeekPattern.forEach(day => {
      csvRows.push(`${day.day},${day.count}`);
    });
    csvRows.push('');

    // Employee Leave Balances
    csvRows.push('EMPLOYEE LEAVE BALANCES');
    csvRows.push('Employee,Email,Entitled Days,Taken Days,Remaining Days');
    balances.forEach(balance => {
      const name = `${balance.user_profiles?.first_name || ''} ${balance.user_profiles?.last_name || ''}`.trim();
      const email = balance.user_profiles?.email || '';
      csvRows.push(`${name},${email},${balance.entitled_days},${balance.taken_days},${balance.remaining_days}`);
    });
    csvRows.push('');

    // All Leave Requests
    csvRows.push('ALL LEAVE REQUESTS');
    csvRows.push('Employee,Email,Type,Status,Start Date,End Date,Days,Created Date,Notes');
    requests.forEach(req => {
      const name = `${req.user_profiles?.first_name || ''} ${req.user_profiles?.last_name || ''}`.trim();
      const email = req.user_profiles?.email || '';
      const type = getRequestTypeLabel(req.leave_type);
      const notes = (req.user_note || '').replace(/,/g, ';');
      const createdDate = req.created_date ? new Date(req.created_date).toLocaleDateString('en-GB') : '';
      csvRows.push(`${name},${email},${type},${req.status},${req.start_date},${req.end_date},${req.calculated_days || ''},${createdDate},"${notes}"`);
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    return true;
  },

  /**
   * Export to Excel (lazy loads xlsx)
   */
  async exportExcel(reportData, companyName, year) {
    // Lazy load xlsx
    const XLSX = await import('xlsx');

    const { metrics, overview, approvalEfficiency, byLeaveType, monthlyTrends,
            forfeitureRisk, upcomingAbsences, atRiskEmployees, peakAbsenceDays,
            dayOfWeekPattern, balances, requests } = reportData;

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Overview Sheet
    const overviewData = [
      ['Attendance Report'],
      ['Company', companyName || ''],
      ['Year', year],
      ['Generated', new Date().toLocaleString('en-GB')],
      [],
      ['KEY METRICS'],
      ['Metric', 'Value'],
      ['Absenteeism Rate', `${metrics.absenteeismRate}%`],
      ['Sick Leave Rate', `${metrics.sickLeaveRate}%`],
      ['Leave Utilization', `${metrics.utilizationRate}%`],
      ['Leave Liability', `£${metrics.leaveLibability.toLocaleString()}`],
      [],
      ['OVERVIEW'],
      ['Metric', 'Value'],
      ['Total Users', overview.totalUsers],
      ['Total Entitled Days', overview.totalEntitled],
      ['Total Taken Days', overview.totalTaken],
      ['Total Remaining Days', overview.totalRemaining],
      ['Total Requests', overview.totalRequests],
      ['Approved Requests', overview.approvedRequests],
      ['Rejected Requests', overview.rejectedRequests],
      ['Pending Requests', overview.pendingRequests],
      [],
      ['APPROVAL EFFICIENCY'],
      ['Metric', 'Value'],
      ['Avg Approval Time', `${approvalEfficiency.avgApprovalTime} hours`],
      ['Pending Requests', overview.pendingRequests],
      ['Overdue (>48h)', approvalEfficiency.pendingOverdue]
    ];
    const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
    XLSX.utils.book_append_sheet(wb, wsOverview, 'Overview');

    // By Leave Type Sheet
    const leaveTypeData = [
      ['Requests by Leave Type'],
      [],
      ['Type', 'Total', 'Approved', 'Rejected', 'Pending'],
      ...byLeaveType.map(t => [t.type, t.count, t.approved, t.rejected, t.pending])
    ];
    const wsLeaveType = XLSX.utils.aoa_to_sheet(leaveTypeData);
    XLSX.utils.book_append_sheet(wb, wsLeaveType, 'By Leave Type');

    // Monthly Trends Sheet
    const monthlyData = [
      ['Monthly Request Trends'],
      [],
      ['Month', 'Total Requests', 'Approved', 'Rejected'],
      ...monthlyTrends.map(m => [m.monthName, m.requests, m.approved, m.rejected])
    ];
    const wsMonthly = XLSX.utils.aoa_to_sheet(monthlyData);
    XLSX.utils.book_append_sheet(wb, wsMonthly, 'Monthly Trends');

    // Forfeiture Risk Sheet
    if (forfeitureRisk.length > 0) {
      const forfeitureData = [
        ['Forfeiture Risk'],
        [],
        ['Employee', 'Days at Risk', 'Days Until Year End'],
        ...forfeitureRisk.map(emp => [emp.name, emp.daysAtRisk, emp.daysUntilYearEnd])
      ];
      const wsForf = XLSX.utils.aoa_to_sheet(forfeitureData);
      XLSX.utils.book_append_sheet(wb, wsForf, 'Forfeiture Risk');
    }

    // Upcoming Absences Sheet
    if (upcomingAbsences.length > 0) {
      const upcomingData = [
        ['Upcoming Absences (Next 30 Days)'],
        [],
        ['Date', 'Employee', 'Type', 'Days'],
        ...upcomingAbsences.map(abs => [abs.date, abs.name, abs.type, abs.days])
      ];
      const wsUpcoming = XLSX.utils.aoa_to_sheet(upcomingData);
      XLSX.utils.book_append_sheet(wb, wsUpcoming, 'Upcoming Absences');
    }

    // At-Risk Employees Sheet
    if (atRiskEmployees.burnoutRisk.length > 0 || atRiskEmployees.lowBalance.length > 0) {
      const atRiskData = [
        ['At-Risk Employees'],
        [],
        ['BURNOUT RISK (Low Leave Usage)'],
        ['Employee', 'Remaining Days', 'Usage Rate'],
        ...atRiskEmployees.burnoutRisk.map(emp => [emp.name, emp.remaining, `${emp.usageRate}%`]),
        [],
        ['LOW BALANCE ALERT'],
        ['Employee', 'Remaining Days', 'Usage Rate'],
        ...atRiskEmployees.lowBalance.map(emp => [emp.name, emp.remaining, `${emp.usageRate}%`])
      ];
      const wsAtRisk = XLSX.utils.aoa_to_sheet(atRiskData);
      XLSX.utils.book_append_sheet(wb, wsAtRisk, 'At-Risk Employees');
    }

    // Peak Absence Days Sheet
    if (peakAbsenceDays.length > 0) {
      const peakData = [
        ['Peak Absence Days'],
        [],
        ['Date', 'Employees Absent'],
        ...peakAbsenceDays.map(peak => [peak.date, peak.count])
      ];
      const wsPeak = XLSX.utils.aoa_to_sheet(peakData);
      XLSX.utils.book_append_sheet(wb, wsPeak, 'Peak Absences');
    }

    // Sick Leave Pattern Sheet
    const patternData = [
      ['Sick Leave Pattern by Day of Week'],
      [],
      ['Day', 'Sick Days'],
      ...dayOfWeekPattern.map(day => [day.day, day.count])
    ];
    const wsPattern = XLSX.utils.aoa_to_sheet(patternData);
    XLSX.utils.book_append_sheet(wb, wsPattern, 'Sick Leave Pattern');

    // Employee Leave Balances Sheet
    if (balances.length > 0) {
      const balancesData = [
        ['Employee Leave Balances'],
        [],
        ['Employee', 'Email', 'Entitled Days', 'Taken Days', 'Remaining Days'],
        ...balances.map(balance => {
          const name = `${balance.user_profiles?.first_name || ''} ${balance.user_profiles?.last_name || ''}`.trim();
          const email = balance.user_profiles?.email || '';
          return [name, email, balance.entitled_days, balance.taken_days, balance.remaining_days];
        })
      ];
      const wsBalances = XLSX.utils.aoa_to_sheet(balancesData);
      XLSX.utils.book_append_sheet(wb, wsBalances, 'Employee Balances');
    }

    // All Leave Requests Sheet
    if (requests.length > 0) {
      const requestsData = [
        ['All Leave Requests'],
        [],
        ['Employee', 'Email', 'Type', 'Status', 'Start Date', 'End Date', 'Days', 'Created Date', 'Notes'],
        ...requests.map(req => {
          const name = `${req.user_profiles?.first_name || ''} ${req.user_profiles?.last_name || ''}`.trim();
          const email = req.user_profiles?.email || '';
          const type = getRequestTypeLabel(req.leave_type);
          const createdDate = req.created_date ? new Date(req.created_date).toLocaleDateString('en-GB') : '';
          return [name, email, type, req.status, req.start_date, req.end_date, req.calculated_days || '', createdDate, req.user_note || ''];
        })
      ];
      const wsRequests = XLSX.utils.aoa_to_sheet(requestsData);
      XLSX.utils.book_append_sheet(wb, wsRequests, 'All Leave Requests');
    }

    // Write file
    XLSX.writeFile(wb, `attendance-report-${year}.xlsx`);

    return true;
  },

  /**
   * Export to PDF (lazy loads jspdf)
   */
  async exportPDF(contentAreaId, year) {
    // Lazy load PDF libraries
    const [{ default: jsPDF }, { toPng }] = await Promise.all([
      import('jspdf'),
      import('html-to-image')
    ]);

    // Get the reports content area by ID
    const contentArea = document.getElementById(contentAreaId);
    if (!contentArea) {
      throw new Error('Could not find content to export');
    }

    // Temporarily hide the header with buttons
    const header = document.getElementById('reports-header');
    const originalHeaderDisplay = header ? header.style.display : '';
    if (header) {
      header.style.display = 'none';
    }

    // Force all SVG elements to have explicit dimensions
    const svgElements = contentArea.querySelectorAll('svg');
    svgElements.forEach(svg => {
      if (!svg.getAttribute('width')) {
        const bbox = svg.getBoundingClientRect();
        svg.setAttribute('width', bbox.width);
        svg.setAttribute('height', bbox.height);
      }
    });

    // Wait for SVGs to render
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pdfWidth - (margin * 2);
    const maxContentHeight = pdfHeight - (margin * 2);

    let currentY = margin;
    let isFirstPage = true;

    // Get all sections
    const cards = Array.from(contentArea.children).filter(child =>
      child.classList && (child.classList.contains('space-y-6') || child.tagName === 'DIV')
    );

    const sections = [];
    cards.forEach(card => {
      if (card.children) {
        sections.push(...Array.from(card.children));
      } else {
        sections.push(card);
      }
    });

    for (const section of sections) {
      // Capture each section individually
      const sectionDataUrl = await toPng(section, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#fcf8f3',
        cacheBust: true,
        skipFonts: false
      });

      const img = new Image();
      img.src = sectionDataUrl;

      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Calculate dimensions
      const sectionHeight = (img.height * contentWidth) / img.width;

      // Check if section fits on current page
      if (!isFirstPage && currentY + sectionHeight > pdfHeight - margin) {
        pdf.addPage();
        currentY = margin;
      }

      // If section is too tall for one page, split it
      if (sectionHeight > maxContentHeight) {
        let remainingHeight = sectionHeight;
        let sourceY = 0;

        while (remainingHeight > 0) {
          if (sourceY > 0) {
            pdf.addPage();
            currentY = margin;
          }

          const heightToAdd = Math.min(remainingHeight, maxContentHeight);
          const sourceHeightPx = (heightToAdd * img.width) / contentWidth;

          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = sourceHeightPx;
          const ctx = canvas.getContext('2d');

          ctx.drawImage(
            img,
            0, sourceY * (img.width / contentWidth),
            img.width, sourceHeightPx,
            0, 0,
            img.width, sourceHeightPx
          );

          const slicedDataUrl = canvas.toDataURL('image/png', 1.0);
          pdf.addImage(slicedDataUrl, 'PNG', margin, currentY, contentWidth, heightToAdd, undefined, 'FAST');

          sourceY += heightToAdd;
          remainingHeight -= heightToAdd;
          currentY = margin;
        }
      } else {
        // Section fits, add it
        pdf.addImage(sectionDataUrl, 'PNG', margin, currentY, contentWidth, sectionHeight, undefined, 'FAST');
        currentY += sectionHeight + 5;
      }

      isFirstPage = false;
    }

    // Restore header
    if (header) {
      header.style.display = originalHeaderDisplay;
    }

    // Download the PDF
    pdf.save(`attendance-report-${year}.pdf`);

    return true;
  }
};
